import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import styled from "styled-components";
import { ErrorMessage, SuccessMessage, WarningMessage } from "../../../_UI/Components/Alerts/PathwayAlertMessages";
import LoadingSpinner from "../../../_UI/Components/LoadingSpinner";
import { PathwayButton, PathwayLinkButton } from "../../../_UI/Components/PathwayButtons";
import PathwayStatusPill from "../../../_UI/Components/PathwayStatusPill";
import { FlexibleButtonModalFooter } from "../../../_UI/StyledComponents/PathwayModal";
import {
    PathwayTable,
    PathwayTableCellContent,
    PathwayTableContentRow,
    PathwayTableHeader,
    PathwayTableHeaderRow,
    TableContentDividerLine,
} from "../../../_UI/StyledComponents/PathwayTable";
import { getIntegrationSquareLogo } from "../../../_statics/IntegrationBrand";
import { checkShopifyWebhookHealth, repairShopifyWebhooks } from "../services/clients.actions";
import { Logo, Mono, Muted } from "./shopifyWebhookHealth.styles";
import {
    WEBHOOK_BUCKET,
    WEBHOOK_TOPIC_STATUS,
    WebhookHealthAction,
    effectiveBucket,
    endpointOf,
    formatCheckedAt,
    notesOf,
    topicLabel,
    topicsOf,
} from "./shopifyWebhookHealth";

const TOPIC_PILL = {
    [WEBHOOK_TOPIC_STATUS.CORRECT]: { severity: "ok", label: "Aktiv" },
    [WEBHOOK_TOPIC_STATUS.MISSING]: { severity: "error", label: "Nicht registriert" },
    [WEBHOOK_TOPIC_STATUS.WRONG_PATH]: { severity: "error", label: "Falsche Adresse" },
    [WEBHOOK_TOPIC_STATUS.WRONG_HOST]: { severity: "error", label: "Falsche Adresse" },
    [WEBHOOK_TOPIC_STATUS.DUPLICATE]: { severity: "warning", label: "Doppelt" },
};

const countByStatus = (topics, statuses) => topics.filter((topic) => statuses.includes(topic.status)).length;

// One sentence per defect class, derived from the topic rows rather than the bucket so a mixed result
// ("1 fehlt, 1 doppelt") is described completely.
export const describeWebhookProblems = (result) => {
    const topics = topicsOf(result);
    const missing = countByStatus(topics, [WEBHOOK_TOPIC_STATUS.MISSING]);
    const wrongAddress = countByStatus(topics, [WEBHOOK_TOPIC_STATUS.WRONG_HOST, WEBHOOK_TOPIC_STATUS.WRONG_PATH]);
    const duplicate = countByStatus(topics, [WEBHOOK_TOPIC_STATUS.DUPLICATE]);

    const sentences = [];
    if (missing > 0) {
        sentences.push(missing === 1 ? "1 Webhook fehlt." : `${missing} Webhooks fehlen.`);
    }
    if (wrongAddress > 0) {
        sentences.push(
            wrongAddress === 1
                ? "1 Webhook zeigt auf eine falsche Adresse."
                : `${wrongAddress} Webhooks zeigen auf eine falsche Adresse.`
        );
    }
    if (duplicate > 0) {
        sentences.push(
            duplicate === 1 ? "1 Webhook ist doppelt registriert." : `${duplicate} Webhooks sind doppelt registriert.`
        );
    }
    return sentences.length > 0 ? sentences.join(" ") : "Nicht alle Webhooks sind korrekt registriert.";
};

const ShopifyWebhookHealthModal = ({ connection, isOpen, onClose }) => {
    const dispatch = useDispatch();

    const authDataUUID = connection ? connection.authDataUUID : null;
    const entry = useSelector((state) =>
        authDataUUID ? state.client.shopifyWebhookHealth.byUUID[authDataUUID] : null
    );
    // Which credential this modal has already asked for. The store keeps the previous result of a credential,
    // so without this the first frame after reopening would show the old one before the new request starts.
    const [startedUUID, setStartedUUID] = useState(null);

    useEffect(() => {
        if (!isOpen || !authDataUUID) {
            return;
        }
        setStartedUUID(authDataUUID);
        dispatch(checkShopifyWebhookHealth(authDataUUID));
    }, [isOpen, authDataUUID, dispatch]);

    const handleClose = () => {
        setStartedUUID(null);
        onClose();
    };

    if (!connection) {
        return null;
    }

    // A response only ever lands under its own credential's key, so an answer to a request this modal no
    // longer has open is simply never read — no cancellation needed.
    const checking = startedUUID !== authDataUUID || !entry || entry.loading;
    // The entry survives the modal being closed, so a stale action must not word the next check's spinner.
    const pendingAction = startedUUID === authDataUUID ? entry?.action : null;
    const repairing = pendingAction === WebhookHealthAction.REPAIR;
    const error = checking ? null : entry.errorMessage;
    // The last result stays on screen next to an error: a failed repair must not take away the topic table
    // that says which webhooks were missing.
    const result = checking ? null : entry.dto;
    const shopDomain = result?.shopDomain ?? connection.shopDomain;
    const bucket = result ? effectiveBucket(result) : null;
    const endpoint = result ? endpointOf(result) : null;
    const canRepair = !!bucket && bucket !== WEBHOOK_BUCKET.CORRECT;

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="lg" data-cy="shopify-webhook-health-modal">
            <ModalHeader toggle={handleClose} tag="div">
                <Eyebrow>Webhook-Status</Eyebrow>
                <Title>{connection.title || shopDomain}</Title>
            </ModalHeader>
            <Body>
                <MetaLine>
                    <Logo $size="18px" src={getIntegrationSquareLogo(connection.authDataType)} alt="" />
                    <span>{shopDomain}</span>
                </MetaLine>

                {checking && (
                    <CheckingState>
                        <LoadingSpinner loading={true} size="var(--iconSize-l)" />
                        <span>
                            {repairing
                                ? "Webhooks werden neu registriert …"
                                : "Webhooks werden bei Shopify abgefragt …"}
                        </span>
                    </CheckingState>
                )}

                {error && (
                    <ErrorMessage marginless>
                        <strong>
                            {pendingAction === WebhookHealthAction.REPAIR
                                ? "Die Registrierung konnte nicht ausgeführt werden."
                                : "Die Prüfung konnte nicht ausgeführt werden."}
                        </strong>
                        <div>{error}</div>
                    </ErrorMessage>
                )}

                {result && (
                    <>
                        {error && (
                            // The result below is the one from before the failed attempt, so it must not read as
                            // the answer to what the user just triggered.
                            <StaleResultNotice>Stand: {formatCheckedAt(result)}</StaleResultNotice>
                        )}
                        <ResultMessage result={result} bucket={bucket} />
                        <TopicTable topics={topicsOf(result)} />
                        {endpoint && (
                            <Endpoint>
                                <EndpointLabel>Endpoint</EndpointLabel>
                                <EndpointValue>{endpoint}</EndpointValue>
                            </Endpoint>
                        )}
                    </>
                )}
            </Body>
            <FlexibleButtonModalFooter>
                <PathwayLinkButton onClick={handleClose} dataCy="shopify-webhook-health-close-button">
                    Schließen
                </PathwayLinkButton>
                <PathwayButton
                    color="secondary"
                    disabled={checking}
                    onClick={() => dispatch(checkShopifyWebhookHealth(authDataUUID))}
                    dataCy="shopify-webhook-health-recheck-button"
                >
                    Erneut prüfen
                </PathwayButton>
                {canRepair && (
                    <PathwayButton
                        color="primary"
                        onClick={() => dispatch(repairShopifyWebhooks(authDataUUID))}
                        dataCy="shopify-webhook-health-repair-button"
                    >
                        Fehlende Webhooks registrieren
                    </PathwayButton>
                )}
            </FlexibleButtonModalFooter>
        </Modal>
    );
};

ShopifyWebhookHealthModal.propTypes = {
    connection: PropTypes.shape({
        authDataUUID: PropTypes.string.isRequired,
        authDataType: PropTypes.string,
        title: PropTypes.string,
        shopDomain: PropTypes.string,
    }),
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default ShopifyWebhookHealthModal;

const Notes = ({ result }) => notesOf(result).map((note) => <div key={note}>{note}</div>);

const ResultMessage = ({ result, bucket }) => {
    if (bucket === WEBHOOK_BUCKET.UNKNOWN) {
        return (
            <WarningMessage marginless>
                <strong>Der Webhook-Status konnte nicht eindeutig bestimmt werden.</strong>
                <div>Bitte die Prüfung wiederholen oder die Webhooks in Shopify kontrollieren.</div>
                <Notes result={result} />
            </WarningMessage>
        );
    }
    if (bucket === WEBHOOK_BUCKET.UNREACHABLE) {
        return (
            <ErrorMessage marginless>
                <strong>Die Webhooks konnten nicht geprüft werden.</strong>
                <Notes result={result} />
            </ErrorMessage>
        );
    }
    if (bucket === WEBHOOK_BUCKET.CORRECT) {
        return (
            <SuccessMessage marginless>
                <strong>Alle Webhooks aktiv.</strong>
                <div>Bestellungen werden automatisch importiert.</div>
                <Notes result={result} />
            </SuccessMessage>
        );
    }
    return (
        <WarningMessage marginless>
            <strong>{describeWebhookProblems(result)}</strong>
            <div>Bestellungen werden dadurch möglicherweise nicht automatisch importiert.</div>
            <Notes result={result} />
        </WarningMessage>
    );
};

// Only a wrong or duplicate registration has an address worth reading; a correct one just repeats the
// endpoint line and a missing one has none.
const RegisteredAddresses = ({ topic }) => {
    const addresses = Array.isArray(topic.actualAddresses) ? topic.actualAddresses : [];
    if (topic.status === WEBHOOK_TOPIC_STATUS.CORRECT || addresses.length === 0) {
        return <Muted>—</Muted>;
    }
    return (
        <AddressList>
            {addresses.map((address, index) => (
                // Keyed by position: a DUPLICATE or WRONG_HOST finding lists one entry per subscription, and
                // two subscriptions on the same stale address are exactly the case this table has to show.
                <li key={topic.topic + "-" + index}>{address}</li>
            ))}
        </AddressList>
    );
};

const TopicTable = ({ topics }) => (
    <InnerTable>
        <TopicHeaderRow>
            <PathwayTableHeader>Topic</PathwayTableHeader>
            <PathwayTableHeader>Status</PathwayTableHeader>
            <PathwayTableHeader>Registrierte Adresse</PathwayTableHeader>
        </TopicHeaderRow>
        {topics.map((topic, index) => {
            const pill = TOPIC_PILL[topic.status] || { severity: "warning", label: topic.status };
            return (
                // The rows are the two audits concatenated, so a topic managed by both would collide on its
                // name alone.
                <React.Fragment key={topic.topic + "-" + index}>
                    <TopicRow data-cy={"shopify-webhook-health-topic-" + topic.topic}>
                        <PathwayTableCellContent>
                            <Mono>{topicLabel(topic.topic)}</Mono>
                        </PathwayTableCellContent>
                        <PathwayTableCellContent>
                            <PathwayStatusPill severity={pill.severity}>{pill.label}</PathwayStatusPill>
                        </PathwayTableCellContent>
                        <PathwayTableCellContent>
                            <RegisteredAddresses topic={topic} />
                        </PathwayTableCellContent>
                    </TopicRow>
                    <TableContentDividerLine />
                </React.Fragment>
            );
        })}
    </InnerTable>
);

const Eyebrow = styled.div`
    font-size: var(--pw-text-2xs);
    font-weight: var(--pw-weight-bold);
    letter-spacing: var(--pw-tracking-eyebrow);
    text-transform: uppercase;
    color: var(--pw-brand-cyan);
`;

const Title = styled.div`
    color: var(--text-heading);
`;

const Body = styled(ModalBody)`
    display: grid;
    row-gap: var(--gap-l);
`;

const MetaLine = styled.div`
    display: flex;
    align-items: center;
    gap: var(--gap-s);
    font-family: var(--font-mono);
    font-size: var(--pw-text-sm);
    color: var(--text-muted);
`;

const StaleResultNotice = styled.div`
    font-size: var(--pw-text-sm);
    color: var(--text-muted);
`;

const CheckingState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--gap-m);
    padding: var(--gap-xxl) 0;
    color: var(--text-muted);
`;

const InnerTable = styled(PathwayTable)`
    border-radius: var(--pw-radius-inner);
    box-shadow: none;
`;

const TOPIC_COLUMNS = "minmax(0, 1fr) 150px minmax(0, 1.6fr)";

const TopicHeaderRow = styled(PathwayTableHeaderRow)`
    grid-template-columns: ${TOPIC_COLUMNS};
    padding: 10px 14px;
    column-gap: var(--gap-m);
`;

const TopicRow = styled(PathwayTableContentRow)`
    grid-template-columns: ${TOPIC_COLUMNS};
    padding: 11px 14px;
    column-gap: var(--gap-m);
`;

const Endpoint = styled.div`
    display: grid;
    row-gap: var(--gap-xs);
`;

const EndpointLabel = styled.span`
    font-size: var(--pw-text-2xs);
    font-weight: var(--pw-weight-bold);
    letter-spacing: var(--pw-tracking-caps);
    text-transform: uppercase;
    color: var(--pw-neutral-400);
`;

const AddressList = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    row-gap: var(--gap-xs);
    font-family: var(--font-mono);
    font-size: var(--pw-text-sm);
    color: var(--text-body);
    word-break: break-all;
`;

const EndpointValue = styled.span`
    font-family: var(--font-mono);
    font-size: var(--pw-text-sm);
    color: var(--text-body);
    word-break: break-all;
`;
